import { inject, Injectable } from "@angular/core";
import { FgdsProductType } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { catchError, defaultIfEmpty, forkJoin, map, Observable, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class PlasticSignDataService {
  private productService = inject(ProductService);

  getPlasticSignData(
    plasticSigns: readonly PlasticSignConfiguration[],
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ProductData[][]> {
    if (plasticSigns.length == 0) {
      return of([]);
    }
    return forkJoin(plasticSigns.map((plasticSign) => this.getProducts(plasticSign, incompleteConfigIds, localeId))).pipe(
      map((products) => products.filter((product): product is ProductData[] => !!product)),
    );
  }

  assignProductToConfiguration(
    configs: PlasticSignConfiguration[],
    uniqueConfigs: PlasticSignConfiguration[],
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ConfigProductMap[]> {
    if (uniqueConfigs.length === 0) {
      return of([]);
    }
    uniqueConfigs.sort((a, b) => parseInt(a.positionNumber!) - parseInt(b.positionNumber!));
    return forkJoin(
      uniqueConfigs.map((uniqueConfig: PlasticSignConfiguration) =>
        this.getProducts(uniqueConfig, incompleteConfigIds, localeId).pipe(
          map((productData) => {
            const placedProductsCount = configs.filter((config) => config.id === uniqueConfig.id).length;
            return new ConfigProductMap(uniqueConfig, productData ?? [], placedProductsCount);
          }),
        ),
      ),
    );
  }

  private getProducts(
    plasticSignsConfig: PlasticSignConfiguration,
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ProductData[] | undefined> {
    return this.productService.getProductById(plasticSignsConfig.productId).pipe(
      map((product) => {
        if (!product || product.type !== FgdsProductType.PLASTICSIGN) {
          return undefined;
        }
        const productData = ProductData.create(
          product,
          localeId,
          this.productService.getFilterCountry()!,
          1,
          ProductListDiscountSlot.MAIN,
        );
        productData.positionId = plasticSignsConfig.positionNumber!;
        productData.isIncompleteConfiguration = incompleteConfigIds.includes(plasticSignsConfig.id);
        return [productData];
      }),
      defaultIfEmpty(undefined),
      catchError(() => of(undefined)),
    );
  }
}
